import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DrawingLayer } from 'business/models/DrawingLayer';
import { LayerDTO } from 'business/models/Models';
import { layerExplorerDefaultSelectionThunk } from 'redux/thunks/layers';

export type LayerState = {
  linkedLayer: DrawingLayer;
  deletedLayers: DrawingLayer[];
  deleteFamily: boolean;
  past: DrawingLayer[][];
  future: DrawingLayer[][];
  layerDesignSelectedTabIndex: ELayerDesignSelectedTabIndex;
};

export enum ELayerDesignSelectedTabIndex {
  Explorer = 0,
  Layers = 1,
}

const initialState = {
  deletedLayers: [],
  past: [],
  future: [],
  layerDesignSelectedTabIndex: ELayerDesignSelectedTabIndex.Explorer,
} as LayerState;

const layerSlice = createSlice({
  name: 'layer',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(layerExplorerDefaultSelectionThunk.fulfilled, (state, action) => {
      state.layerDesignSelectedTabIndex = action.payload
        ? ELayerDesignSelectedTabIndex.Explorer
        : ELayerDesignSelectedTabIndex.Layers;
    });
  },
  reducers: {
    SetLinkedLayers: (state, action: PayloadAction<LayerDTO>): void => {
      state.linkedLayer = action.payload;
    },
    setDeleteLayer: (state, action: PayloadAction<DrawingLayer>): void => {
      const tempDeletedLayer = [...state.deletedLayers];
      tempDeletedLayer.push(action.payload);
      state.deletedLayers = [...tempDeletedLayer];
    },
    setDeleteLayerClear: (state): void => {
      const tempDeletedLayer = [];
      state.deletedLayers = [...tempDeletedLayer];
      state.past = [];
      state.future = [];
    },
    setDeleteFamily: (state, action: PayloadAction<boolean>): void => {
      state.deleteFamily = action.payload;
    },
    popDeletedLayers: (state): void => {
      const tempDeletedLayer = [...state.deletedLayers];
      tempDeletedLayer.pop();
      state.deletedLayers = [...tempDeletedLayer];
    },
    preserveDeletedLayerUndoableState: (state): void => {
      state.past = [...state.past, state.deletedLayers];
    },
    performDeletedLayerUndo: (state): void => {
      if (!state.past || !state.past.length) {
        return;
      }
      const tempPast = state.past;
      const poppedElement = tempPast.pop();
      const newFuture = [...state.future, poppedElement];

      state.past = tempPast;
      if (tempPast[tempPast.length - 1] !== undefined) {
        state.deletedLayers = tempPast[tempPast.length - 1];
      } else {
        state.deletedLayers = [];
      }

      state.future = newFuture;
    },
    performDeletedLayerRedo: (state): void => {
      if (!state.future || !state.future.length) {
        return;
      }

      const tempFuture = state.future;
      const poppedElement = tempFuture.pop();
      const newPast = [...state.past, poppedElement];

      state.future = tempFuture;
      state.past = newPast;
      state.deletedLayers = poppedElement;
    },
    setLayerDesignSelectedTabIndex: (
      state,
      action: PayloadAction<ELayerDesignSelectedTabIndex>,
    ): void => {
      state.layerDesignSelectedTabIndex = action.payload;
    },
  },
});

export const {
  SetLinkedLayers,
  setDeleteLayer,
  setDeleteLayerClear,
  setDeleteFamily,
  popDeletedLayers,
  preserveDeletedLayerUndoableState,
  performDeletedLayerUndo,
  performDeletedLayerRedo,
  setLayerDesignSelectedTabIndex,
} = layerSlice.actions;

export default layerSlice.reducer;
